import { Alert, useTheme } from "@mui/material";
import * as React from "react";

import { MetadataForm, MetadataView } from "@/shared/components/Metadata";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { Device } from "@/shared/domain/devices";
import { useUpdateDevice } from "@/shared/domain/devices/hooks/useUpdateDevice";

interface UpdateDeviceMetadataProps {
  device: Device;
  resourceOwnerId: string;
  readonly?: boolean;
  onMetadataUpdated?: (device: Device) => void;
  onError?: (error: Error) => void;
}

export const UpdateDeviceMetadata: React.FC<UpdateDeviceMetadataProps> = ({
  device,
  resourceOwnerId,
  readonly = false,
  onMetadataUpdated,
  onError,
}) => {
  const theme = useTheme();
  const updateDeviceMutation = useUpdateDevice();

  const addMetadata = (key: string, value: unknown) => {
    updateDeviceMutation.mutate(
      {
        deviceId: device.device_id,
        resourceOwnerId,
        updates: {
          metadata_changeset: {
            put_fields: { [key]: value },
          },
        },
      },
      {
        onSuccess: (updatedDevice) => {
          if (onMetadataUpdated) {
            onMetadataUpdated(updatedDevice);
          }
        },
        onError: (error: Error) => {
          if (onError) {
            onError(error);
          }
        },
      },
    );
  };

  const removeMetadata = (key: string) => {
    updateDeviceMutation.mutate(
      {
        deviceId: device.device_id,
        resourceOwnerId,
        updates: {
          metadata_changeset: {
            remove_fields: [key],
          },
        },
      },
      {
        onSuccess: (updatedDevice) => {
          if (onMetadataUpdated) {
            onMetadataUpdated(updatedDevice);
          }
        },
        onError: (error: Error) => {
          if (onError) {
            onError(error);
          }
        },
      },
    );
  };

  return (
    <>
      {readonly && Object.keys(device.metadata).length === 0 && <>None</>}
      <MetadataView
        key={device.device_id}
        metadata={device.metadata}
        onDeleteMetadata={readonly ? undefined : removeMetadata}
        disabled={readonly}
      />
      {readonly === false && (
        <MetadataForm
          key={`${device.device_id}-form`}
          metadata={device.metadata}
          autocompleteType={AutocompleteType.GenericMetadataKeys}
          onAddMetadata={addMetadata}
          disabled={updateDeviceMutation.isPending}
          isLoading={updateDeviceMutation.isPending}
        />
      )}

      {updateDeviceMutation.isError && (
        <Alert
          onClose={() => updateDeviceMutation.reset()}
          severity="error"
          sx={{ marginTop: theme.spacing(1) }}
        >
          {updateDeviceMutation.error.message}
        </Alert>
      )}
    </>
  );
};
